/**
 * ReservationCell - Cel·la modular per al calendari
 * Sprint 27: icones WhatsApp/🔑, camps nous (import, idioma, nits, hora)
 * Repàs visual (`plans/calendar-visual-refresh.md` §6): fons blanc, barra de canal del tema,
 * línia 2 sense el nom del canal, vores tallades, badge de grup i cancel·lades ratllades.
 * Senyals (`plans/calendar-card-signals.md` §6 F1, variant L): franja de pagament enganxada a la barra
 * del canal, tres llums a la dreta, G fosc/blanc, ★ i ↔ a la línia 1 i la via a la línia 3.
 * Bloqueig (`plans/reservation-move-lock.md` §4 F.2): 🔒 gris al costat del nom, o la pastilla
 * vermella «🔒 Moguda» si Beds24 l'ha moguda estant bloquejada.
 * Mobile-first, touch-friendly, SVG icons
 */

import { cn } from '@/lib/utils';
import { formatCalendarDate } from '@/lib/dates';
import { BedIcons, type BedType } from './BedIcon';
import type { StatusLevel } from './StatusIcon';
import { BED_AUTO_TEXT, bedWarningText, type BedConfigSource, type BedWarning } from './bed-review';
import { CALENDAR_PALETTE, useCalendarTheme } from './calendar-theme';
import { stayCornerStyle } from './calendar-radius';
import { PaymentStripe } from './PaymentStripe';
import { CardLights } from './CardLights';
import {
  CONTINUES_STAY_MARK,
  GROUP_BADGE_STYLES,
  MOVE_LOCK_ALERT_MARK,
  MOVE_LOCK_MARK,
  PAY_ON_ARRIVAL_MARK,
  PAYMENT_VIA_STYLES,
} from './signal-styles';
import type { BookingSignals } from '@guilleumes/types';

// ============================================
// TYPES
// ============================================

export interface ReservationCellData {
  id: string;
  beds24Id?: string;
  guestName: string;
  channel: string;

  // Dates per posicionament multi-dia
  checkIn: string;
  checkOut: string;

  // Habitació per agrupar a la fila correcta
  unitId: string;

  // Guests
  guestsRegistered: number;
  guestsExpected: number;
  numAdults: number;
  numChildren: number;
  primaryGuestName?: string | null;

  // Beds
  beds?: BedType[];
  /** Raw bedConfig JSON de la reserva (null = usa defaultBedConfig de la unit) */
  bedConfigRaw?: string | null;
  /** Runbook 10 — 'auto' = omplerts per la preferència del client (vora puntejada) */
  bedConfigSource?: BedConfigSource | null;
  /** Runbook 10 — preferència de llit del client */
  bedPreference?: 'twin' | 'double' | 'other' | null;
  /** Runbook 10 — avís calculat pel backend (icona ⚠) */
  bedWarning?: BedWarning | null;
  /** Runbook 20 — «Llits per revisar», decidit pel backend (botó i filtre de la barra) */
  bedReviewPending?: boolean;

  // Status icons (legacy — mantinguts per compatibilitat; `keyStatus` el llegeix `ReservationSheet`)
  keyStatus?: StatusLevel;
  doorStatus?: StatusLevel;
  paymentStatus?: StatusLevel;
  taxStatus?: StatusLevel;

  // 🆕 Sprint 27 — Icones noves
  /**
   * WhatsApp de l'hoste (`reservations.has_whatsapp`, no «missatge enviat»): true = té WhatsApp,
   * false = confirmat que no en té, null = no se sap. La card ja no el pinta (ho diu el llum de baix,
   * `plans/calendar-card-signals.md` §6 F1.9); el llegeix el popover.
   */
  whatsappSent?: boolean | null;
  /** Pagament: true = pagat, false = pendent, null = desconegut (ja no es pinta a la card, D3) */
  isPaid?: boolean | null;
  /** Import total de la reserva */
  totalPrice?: number | null;
  // 🆕 Sprint 29 — Payment tracking
  /** Estat de pagament raw de la BD (PENDING, PAID, PARTIAL, REFUNDED) */
  paymentStatusRaw?: 'PENDING' | 'PAID' | 'PARTIAL' | 'REFUNDED' | null;
  /** Mètode de pagament (CASH, CARD, TRANSFER, STRIPE, BOOKING_OTA, EXPEDIA_OTA) */
  paymentMethod?: 'CASH' | 'CARD' | 'TRANSFER' | 'STRIPE' | 'BOOKING_OTA' | 'EXPEDIA_OTA' | null;
  /** Balanç pendent (totalPrice - deposit) */
  balance?: number | null;
  /** Dipòsit / bestreta rebuda */
  deposit?: number | null;
  /** Data de pagament */
  paidAt?: string | null;
  /** Idioma ISO 639-1 (ca, es, en, fr, de...) */
  lang?: string | null;
  /** Nombre de nits */
  numNights?: number | null;
  /** Hora d'arribada prevista (HH:MM) */
  arrivalTime?: string | null;

  // Badges
  companyName?: string;
  isVip?: boolean;
  /** La card no el pinta: el badge de grup surt de `groupSize` (§6.7) */
  isGroup?: boolean;
  hasPet?: boolean;

  // Visual
  status?: 'confirmed' | 'pending' | 'cancelled' | 'checked_in' | 'checked_out' | 'no_show';

  // Info canal Beds24 (readonly al Sheet)
  referer?: string | null;
  apiSource?: string | null;

  // Telèfon del hoste
  guestPhone?: string | null;
  /** E.164 del backend; null = sense prefix internacional o farciment (Trucar desactivat) */
  guestPhoneE164?: string | null;
  /** Propietat de Beds24 (el botó Codi només surt a la 18770) */
  beds24PropertyId?: number | null;

  // País del hoste (nom i ISO 2)
  guestCountry?: string | null;
  guestCountry2?: string | null;

  // Presentació (`plans/calendar-visual-refresh.md` §4): els posa la graella o la pàgina, no l'API
  /** L'entrada és abans del primer dia visible (vora esquerra tallada) */
  clipStart?: boolean;
  /** La sortida és després de l'últim dia visible (vora dreta tallada) */
  clipEnd?: boolean;
  /** Habitacions del grup de Beds24 (mestra + filles vives); null/absent = no és grup */
  groupSize?: number | null;
  /**
   * Línies de la card segons l'espai (F7, `cardLines` a `grid/row-layout.ts`): 3 = nom · 👥 · import
   * i llits; 2 = sense la línia 👥 (el seu text va al `title` del nom). Absent = 3.
   */
  lines?: 2 | 3;
  /** La card fa < 60 px (`cardDense`): padding i nom més petits perquè hi càpiga tot (F7). */
  dense?: boolean;
  /**
   * Senyals de la card (`plans/calendar-card-signals.md` §2): franja de pagament i tres llums.
   * Absent = sense senyals (calendari històric, reserva creada des del DTO).
   */
  signals?: BookingSignals;
  /** Mida dels llums en px (`cardLightSize`, la posa la graella). Absent = 16. */
  lightSize?: number;
  /** Amplada de la card en px (la posa la graella): < 110 un sol llum, < 60 cap. Absent = ampla. */
  cardWidth?: number;
  /** La graella hi pinta la nansa d'estirar (8 px a la dreta): els llums s'hi aparten. */
  resizeHandle?: boolean;
}

interface ReservationCellProps {
  data: ReservationCellData;
  /** onClick retorna l'id de la reserva. Si no es passa, el Popover gestiona el clic. */
  onClick?: (id: string) => void;
  className?: string;
}

// ============================================
// HELPERS
// ============================================

/**
 * Retorna els últims 4 dígits del beds24Id (codi d'accés TTLock / JOAN)
 */
function getAccessCode(beds24Id?: string): string | null {
  if (!beds24Id) return null;
  const digits = beds24Id.replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
}

/**
 * Determina si avui és el dia de check-in (per mostrar la clau)
 */
function isCheckInToday(checkIn: string): boolean {
  const today = new Date();
  const ci = new Date(checkIn);
  return (
    ci.getFullYear() === today.getFullYear() &&
    ci.getMonth() === today.getMonth() &&
    ci.getDate() === today.getDate()
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** «0 registrats de 2 adults + 2 nens · 1 nit · idioma FR · Booking.com» (el `title` de la línia 2). */
function buildSummaryTitle(data: ReservationCellData): string {
  const guests =
    `${plural(data.guestsRegistered, 'registrat', 'registrats')} de ` +
    plural(data.numAdults, 'adult', 'adults') +
    (data.numChildren > 0 ? ` + ${plural(data.numChildren, 'nen', 'nens')}` : '');
  const parts = [guests];
  if (data.numNights != null) parts.push(plural(data.numNights, 'nit', 'nits'));
  if (data.lang) parts.push(`idioma ${data.lang.toUpperCase()}`);
  if (data.channel) parts.push(data.channel);
  return parts.join(' · ');
}

/** `2026-09-28` → «28 de set.», `2026-10-03` → «3 d’oct.» */
const BED_TITLE_LABELS: Record<BedType, string> = {
  single: 'individual',
  double: 'doble',
  crib: 'bressol',
  bunk: 'llitera',
  extra: 'supletori',
};

/** `title` dels llits: «Llits: doble, supletori» (§6.4, cada icona amb el seu text). */
function bedsTitle(beds: BedType[]): string {
  return `Llits: ${beds.map((type) => BED_TITLE_LABELS[type] ?? type).join(', ')}`;
}

/**
 * Per sota d'aquesta amplada (la mateixa del «un sol llum» de `CardLights`), si la card porta el 🔒,
 * ★ i ↔ no es pinten: el 🔒 té prioritat (§4 F.2). El «G» dels grups es queda.
 */
export const MOVE_LOCK_NARROW_CARD_PX = 110;

function formatShortDate(day: string): string {
  return formatCalendarDate(day, 'ca', "d 'de' MMM");
}

/** `title` del distintiu de grup (§6 F1.7): la principal, fosc; la resta, blanc. */
function groupBadgeTitle(count: number, isMaster: boolean): string {
  return isMaster
    ? `${GROUP_BADGE_STYLES.master.label} de ${count} habitacions`
    : `${GROUP_BADGE_STYLES.member.label} (${count} habitacions)`;
}

// ============================================
// COMPONENT
// ============================================

/*
 * Comptes d'alçada de la card (F7). Vora 1 + 1 = 2 px. Alçades de línia: nom 14 px `leading-tight`
 * = 17,5 (dens: 13 px = 16,25; els badges fan 10 + 4 + 2 = 16); línia 👥 = 14 (el text de 11 px
 * `leading-tight` fa 13,75); línia 3 = 14 (llits; el 🔑 fa 13,25) o 17 amb la vora puntejada dels
 * llits automàtics (1,5 × 2), 16 en 2 línies (vora d'1 px).
 *
 * | Card                             | Caixa | Padding    | Contingut               | Aire  |
 * |----------------------------------|-------|------------|-------------------------|-------|
 * | Normal `full`, 3 línies          | 67    | 8 (py-1)   | 17,5 + 14 + 17 = 48,5   | 8,5   |
 * | Normal `live`, 3 línies, densa   | 55    | 4 (py-0.5) | 16,25 + 14 + 17 = 47,25 | 1,75  |
 * | Compacta `full`, 3 línies, densa | 56    | 4 (py-0.5) | 47,25                   | 2,75  |
 * | Compacta `live`, 2 línies        | 48    | 0 (py-0)   | 16,25 + 16 = 32,25      | 13,75 |
 * | Mínima `full`, 2 línies          | 42    | 0          | 32,25                   | 7,75  |
 * | Mínima `live`, 2 línies          | 36    | 0          | 32,25                   | 1,75  |
 *
 * (Aire = caixa − 2 de vora − padding − contingut, amb el pitjor cas: llits automàtics i badge.)
 * En 2 línies l'aire es reparteix amb `justify-evenly`.
 *
 * En amplada (senyals, §6 F1): barra del canal · franja de pagament (8 px, si n'hi ha) · contingut ·
 * llums (mida + 3 px, o + 8 px amb la nansa d'estirar). El contingut es retalla (`overflow-hidden`):
 * a la línia 3, per prioritat, import > via > llits > 🔑 > hora d'arribada.
 */

export function ReservationCell({ data, onClick, className }: ReservationCellProps) {
  const {
    id,
    beds24Id,
    guestName,
    channel,
    guestsRegistered,
    numAdults,
    numChildren,
    primaryGuestName,
    beds = [],
    bedConfigSource,
    bedWarning,
    companyName,
    isVip,
    hasPet,
    status = 'confirmed',
    totalPrice,
    lang,
    numNights,
    arrivalTime,
    checkIn,
    checkOut,
    clipStart,
    clipEnd,
    groupSize,
    lines = 3,
    dense = false,
    signals,
    lightSize,
    cardWidth,
    resizeHandle,
  } = data;

  const theme = useCalendarTheme();
  const isCancelled = status === 'cancelled';
  const strike = isCancelled && 'line-through';
  const channelLabel = channel || 'Canal desconegut';

  // Codi d'accés: últims 4 dígits beds24Id, NOMÉS si avui és check-in (i la reserva és viva)
  const accessCode = !isCancelled && isCheckInToday(checkIn) ? getAccessCode(beds24Id) : null;

  // Senyals (§6 F1): sense `signals` (calendari històric, reserva del DTO) la card es pinta com abans.
  // Les cancel·lades no en porten mai.
  const cardSignals = isCancelled ? undefined : signals;
  const via = cardSignals?.payment.via ? PAYMENT_VIA_STYLES[cardSignals.payment.via] : null;

  // Runbook 10 — llits automàtics i avís (el text el dona C4; mai el nom del client)
  const bedsAuto = bedConfigSource === 'auto';
  const warningText = bedWarningText(bedWarning);

  // Grup (§6 F1.7): amb senyals, la mida és la del backend (inclou les del grup fora del rang) i la
  // principal va en fosc; sense, la de la graella i en blanc.
  const groupCount = cardSignals
    ? cardSignals.group.size >= 2
      ? cardSignals.group.size
      : null
    : groupSize != null && groupSize >= 2
      ? groupSize
      : null;
  const groupIsMaster = cardSignals?.group.isMaster ?? false;
  // Bloqueig (§4 F.2): amb avís, la pastilla «🔒 Moguda»; si no, el 🔒 gris. Si la card és estreta,
  // ★ i ↔ li deixen el lloc.
  const moveLockReason = cardSignals?.moveLock?.reason ?? null;
  const moveLockAlert = cardSignals?.moveLock?.alert ?? null;
  const hasLockMark = !!(moveLockReason || moveLockAlert);
  const lockCrowds = hasLockMark && cardWidth != null && cardWidth < MOVE_LOCK_NARROW_CARD_PX;
  const payOnArrival = !lockCrowds && (cardSignals?.payOnArrival ?? false);
  const continuesStay = !lockCrowds && (cardSignals?.continuesPreviousStay ?? false);
  const summaryTitle = buildSummaryTitle(data);
  // F7: en 2 línies la línia 👥 no es pinta; el seu text va al `title` del nom. En 2 línies la card
  // sempre és densa.
  const twoLines = lines === 2;
  const compact = dense || twoLines;

  const handleClick = () => {
    onClick?.(id);
  };

  return (
    <div
      onClick={handleClick}
      data-status={status}
      // `useGridPointer` el llegeix per avisar si s'intenta arrossegar una bloquejada.
      data-move-lock={moveLockReason ?? undefined}
      className={cn(
        // Base: fons blanc sempre (D1), vora i ombra del tema
        'flex h-[70px] overflow-hidden rounded-lg border shadow-sm',
        // Vores tallades: la reserva continua fora del rang visible. El radi de debò surt del tema
        // (`stayCornerStyle`, a l'`style`): el de l'esquerra a l'entrada, el de la dreta (recte per
        // defecte, D3) a la sortida, i recte al tall.
        clipStart && 'rounded-l-none',
        clipEnd && 'rounded-r-none',
        // Touch & Hover
        'cursor-pointer touch-manipulation',
        'hover:shadow-md active:scale-[0.98] transition-all duration-150',
        isCancelled && 'opacity-60',
        className,
      )}
      style={{
        backgroundColor: isCancelled ? CALENDAR_PALETTE.cardCancelled : CALENDAR_PALETTE.card,
        borderColor: CALENDAR_PALETTE.cardBorder,
        ...stayCornerStyle(theme.cardRadius, theme.rightRadius, data),
      }}
    >
      {/* Barra del canal (color i gruix del tema) */}
      <div
        role="img"
        className="flex-shrink-0"
        style={{ width: theme.barWidth, backgroundColor: theme.channelColor(channel) }}
        title={channelLabel}
        aria-label={`Canal: ${channelLabel}`}
        data-channel-bar
      />

      {/* Franja de pagament (D1): enganxada a la barra, sense separació; res si és `NONE` */}
      {cardSignals && <PaymentStripe payment={cardSignals.payment} />}

      {clipStart && (
        <ClipMark side="start" title={`Va entrar el ${formatShortDate(checkIn)}`} />
      )}

      {/* Content */}
      <div
        className={cn(
          'flex-1 px-1.5 flex flex-col min-w-0 overflow-hidden',
          twoLines ? 'py-0 justify-evenly' : compact ? 'py-0.5 justify-between' : 'py-1 justify-between',
        )}
        data-card-lines={lines}
      >
        {/* Línia 1: ↔ nom ★ + badges */}
        <div className="flex items-start w-full gap-1 min-w-0 overflow-hidden">
          {continuesStay && (
            <span
              className="flex-shrink-0 text-[12px] font-bold leading-tight"
              style={{ color: CONTINUES_STAY_MARK.color }}
              title={CONTINUES_STAY_MARK.label}
              aria-label={CONTINUES_STAY_MARK.label}
              data-mark="continues-stay"
            >
              {CONTINUES_STAY_MARK.glyph}
            </span>
          )}
          <span
            className={cn(
              'font-bold text-gray-900 truncate leading-tight flex-1 min-w-0',
              compact ? 'text-[13px]' : 'text-sm',
              strike,
            )}
            title={twoLines ? `${guestName} · ${summaryTitle}` : undefined}
            data-cell-text="name"
          >
            {guestName}
          </span>
          {moveLockAlert ? (
            <span
              className="flex-shrink-0 rounded px-1 text-[10px] font-semibold leading-4 whitespace-nowrap"
              style={{ background: MOVE_LOCK_ALERT_MARK.background, color: MOVE_LOCK_ALERT_MARK.color }}
              title={moveLockAlert}
              aria-label={moveLockAlert}
              data-mark="move-lock-alert"
            >
              {MOVE_LOCK_ALERT_MARK.glyph} {MOVE_LOCK_ALERT_MARK.text}
            </span>
          ) : (
            moveLockReason && (
              <span
                className="flex-shrink-0 text-[11px] leading-tight"
                style={{ color: MOVE_LOCK_MARK.color }}
                title={`Bloquejada: ${moveLockReason}`}
                aria-label={`Bloquejada: ${moveLockReason}`}
                data-mark="move-lock"
              >
                {MOVE_LOCK_MARK.glyph}
              </span>
            )
          )}
          {payOnArrival && (
            <span
              className="flex-shrink-0 text-[12px] leading-tight"
              style={{ color: PAY_ON_ARRIVAL_MARK.color }}
              title={PAY_ON_ARRIVAL_MARK.label}
              aria-label={PAY_ON_ARRIVAL_MARK.label}
              data-mark="pay-on-arrival"
            >
              {PAY_ON_ARRIVAL_MARK.glyph}
            </span>
          )}
          {(groupCount != null || isVip || companyName || hasPet) && (
            <div className="flex gap-0.5 flex-shrink-0">
              {groupCount != null && <GroupBadge count={groupCount} isMaster={groupIsMaster} />}
              {isVip && <Badge variant="vip">VIP</Badge>}
              {companyName && (
                <Badge variant="company" title={companyName}>
                  {companyName}
                  {hasPet && <span className="ml-0.5">🐕</span>}
                </Badge>
              )}
              {hasPet && !companyName && (
                <Badge variant="default" title="Amb mascota">
                  🐕
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Línia 2: 👥 0/2+2 · 1n · FR (el canal ja el diu la barra). En 2 línies, no. */}
        {!twoLines && (
          <div className="flex items-center gap-1 min-w-0">
            <span
              className={cn('text-[11px] leading-tight truncate flex-1 min-w-0', strike)}
              style={{ color: CALENDAR_PALETTE.cardSecondaryText }}
              title={summaryTitle}
              data-cell-text="summary"
            >
              👥 {guestsRegistered}/{numAdults}
              {numChildren > 0 ? `+${numChildren}` : ''}
              {numNights != null && ` · ${numNights}n`}
              {lang && ` · ${lang.toUpperCase()}`}
            </span>
          </div>
        )}

        {/* Línia 3, per prioritat (el que no hi cap es retalla per la dreta):
            import · via · llits · ⚠ · 🔑 · hora d'arribada */}
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden leading-tight">
          {totalPrice != null && (
            <span
              className={cn('text-[10px] text-gray-600 font-medium flex-shrink-0', strike)}
              data-cell-text="price"
            >
              {totalPrice.toFixed(0)}€
            </span>
          )}
          {via && (
            <span
              className="flex-shrink-0 rounded-[3px] border border-slate-200 bg-slate-100 px-[3px] text-[10px] font-medium leading-tight text-slate-700"
              title={via.label}
              aria-label={via.label}
              data-payment-via={cardSignals?.payment.via ?? undefined}
            >
              {via.short}
            </span>
          )}
          {beds.length > 0 && (
            <span
              className={cn('flex-shrink-0 inline-flex', bedsAuto && 'border-dashed rounded px-0.5')}
              style={
                bedsAuto
                  ? { borderWidth: twoLines ? 1 : 1.5, borderColor: CALENDAR_PALETTE.bedAutoBorder }
                  : undefined
              }
              title={bedsAuto ? BED_AUTO_TEXT : bedsTitle(beds)}
              data-bed-auto={bedsAuto || undefined}
            >
              <BedIcons beds={beds} muted={isCancelled} />
            </span>
          )}
          {warningText && (
            <span
              className="text-[10px] leading-none text-amber-600 flex-shrink-0"
              title={warningText}
              aria-label={warningText}
              data-bed-warning
            >
              ⚠
            </span>
          )}
          {accessCode && (
            <span
              className="text-[9px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded px-0.5 leading-tight flex-shrink-0"
              title={`Codi accés: ${accessCode}`}
              data-access-code
            >
              🔑{accessCode}
            </span>
          )}
          {arrivalTime && (
            <span
              className={cn('text-[10px] text-blue-600 font-medium flex-shrink-0', strike)}
              title={`Hora d'arribada: ${arrivalTime}`}
              data-cell-text="arrival"
            >
              🕐 {arrivalTime}
            </span>
          )}
          {primaryGuestName && !arrivalTime && !totalPrice && (
            <span
              className={cn('text-[10px] text-blue-600 font-medium truncate min-w-0', strike)}
              data-cell-text="primary-guest"
            >
              👤 {primaryGuestName}
            </span>
          )}
        </div>
      </div>

      {/* Tres llums (D1): dalt entrada/sortida · mig registre · baix contacte */}
      {cardSignals && (
        <CardLights
          signals={cardSignals}
          size={lightSize}
          cardWidth={cardWidth}
          resizeHandle={resizeHandle}
        />
      )}

      {clipEnd && <ClipMark side="end" title={`Surt el ${formatShortDate(checkOut)}`} />}
    </div>
  );
}

// ============================================
// CLIP MARK SUBCOMPONENT
// ============================================

/** `‹` / `›` petit a la vora tallada: la reserva continua fora del rang visible (§6.8). */
function ClipMark({ side, title }: { side: 'start' | 'end'; title: string }) {
  return (
    <span
      className="flex flex-shrink-0 items-center px-0.5 text-[11px] font-bold leading-none"
      style={{ color: CALENDAR_PALETTE.cardSecondaryText }}
      title={title}
      aria-label={title}
      data-clip={side}
    >
      {side === 'start' ? '‹' : '›'}
    </span>
  );
}

// ============================================
// GROUP BADGE SUBCOMPONENT
// ============================================

/** «G{n}» (§6 F1.7): fosc a la reserva principal del grup, blanc amb vora a la resta. */
function GroupBadge({ count, isMaster }: { count: number; isMaster: boolean }) {
  const style = isMaster ? GROUP_BADGE_STYLES.master : GROUP_BADGE_STYLES.member;
  return (
    <span
      className="text-[10px] px-1 py-0.5 rounded font-bold border leading-none"
      style={{ background: style.background, color: style.color, borderColor: style.borderColor }}
      title={groupBadgeTitle(count, isMaster)}
      data-badge="group"
      data-group-master={isMaster ? 'true' : 'false'}
    >
      G{count}
    </span>
  );
}

// ============================================
// BADGE SUBCOMPONENT
// ============================================

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'vip' | 'company';
  title?: string;
}

function Badge({ children, variant = 'default', title }: BadgeProps) {
  const variants = {
    default: 'bg-gray-100 text-gray-600 border-gray-200',
    vip: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    company: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  return (
    <span
      className={cn(
        'text-[10px] px-1 py-0.5 rounded font-medium border leading-none',
        'max-w-[80px] truncate',
        variants[variant],
      )}
      title={title}
      data-badge={variant}
    >
      {children}
    </span>
  );
}

// ============================================
// EXPORTS
// ============================================

export default ReservationCell;
